import React, {useState} from "react";
import PropTypes from "prop-types";
import {FormattedMessage, useIntl} from "react-intl";

import {IconButton, TextField, Tooltip} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";

import {greyishBlue600} from "assets/jss/main";

import {factLabel} from "views/personalShopper/components/formatters";

import SideCard from "./SideCard";

FactsCard.propTypes = {
    facts: PropTypes.arrayOf(PropTypes.object).isRequired,
    onSave: PropTypes.func.isRequired,
    onDelete: PropTypes.func.isRequired,
};

const LABEL_PATTERN = /^[a-z0-9_]{1,64}$/;

function normalizeLabel(raw) {
    return raw
        .trim()
        .toLowerCase()
        .normalize("NFD")
        .replace(/[̀-ͯ]/g, "")
        .replace(/[^a-z0-9]+/g, "_")
        .replace(/^_+|_+$/g, "")
        .slice(0, 64);
}

/**
 * The durable facts the assistant remembered (sizes, family, birthdays, brands liked).
 * The team can correct, add or remove one: the assistant reads them before every answer.
 */
function FactsCard({facts, onSave, onDelete}) {
    const intl = useIntl();
    const [editing, setEditing] = useState(null);
    const [draftLabel, setDraftLabel] = useState("");
    const [draftValue, setDraftValue] = useState("");
    const [error, setError] = useState(null);

    const startAdd = () => {
        setEditing("__new__");
        setDraftLabel("");
        setDraftValue("");
        setError(null);
    };
    const startEdit = (fact) => {
        setEditing(fact.label);
        setDraftLabel(fact.label);
        setDraftValue(fact.value);
        setError(null);
    };
    const cancel = () => setEditing(null);
    const save = () => {
        const label = editing === "__new__" ? normalizeLabel(draftLabel) : editing;
        if (!LABEL_PATTERN.test(label) || draftValue.trim() === "") {
            setError(intl.formatMessage({id: "personalShopper.facts.invalid"}));
            return;
        }
        onSave(label, draftValue.trim())
            .then(() => setEditing(null))
            .catch((requestError) => setError(requestError.message));
    };

    return (
        <SideCard
            titleId="personalShopper.facts.title"
            action={(
                <Tooltip title={intl.formatMessage({id: "personalShopper.facts.add"})}>
                    <IconButton size="small" onClick={startAdd}><AddIcon fontSize="small" /></IconButton>
                </Tooltip>
            )}
        >
            {facts.length === 0 && editing !== "__new__" && (
                <div style={{color: greyishBlue600, fontSize: "13px"}}><FormattedMessage id="personalShopper.facts.empty" /></div>
            )}
            <table style={{width: "100%", fontSize: "14px", borderCollapse: "collapse"}}>
                <tbody>
                    {facts.map((fact) => (
                        <tr key={fact.label}>
                            <td style={{padding: "4px 8px 4px 0", fontWeight: 600, verticalAlign: "top", width: "40%"}}>{factLabel(fact.label)}</td>
                            <td style={{padding: "4px 0", verticalAlign: "top"}}>
                                {editing === fact.label ? (
                                    <TextField size="small" fullWidth value={draftValue} onChange={(event) => setDraftValue(event.target.value)} autoFocus />
                                ) : fact.value}
                            </td>
                            <td style={{padding: "4px 0", whiteSpace: "nowrap", textAlign: "right", verticalAlign: "top"}}>
                                {editing === fact.label ? (
                                    <>
                                        <IconButton size="small" onClick={save}><CheckIcon fontSize="small" /></IconButton>
                                        <IconButton size="small" onClick={cancel}><CloseIcon fontSize="small" /></IconButton>
                                    </>
                                ) : (
                                    <>
                                        <IconButton size="small" onClick={() => startEdit(fact)}><EditOutlinedIcon fontSize="small" /></IconButton>
                                        <IconButton size="small" onClick={() => onDelete(fact.label).catch((requestError) => setError(requestError.message))}><DeleteOutlineIcon fontSize="small" /></IconButton>
                                    </>
                                )}
                            </td>
                        </tr>
                    ))}
                    {editing === "__new__" && (
                        <tr>
                            <td style={{padding: "4px 8px 4px 0", verticalAlign: "top"}}>
                                <TextField size="small" fullWidth placeholder={intl.formatMessage({id: "personalShopper.facts.labelPlaceholder"})} value={draftLabel} onChange={(event) => setDraftLabel(event.target.value)} autoFocus />
                            </td>
                            <td style={{padding: "4px 0", verticalAlign: "top"}}>
                                <TextField size="small" fullWidth placeholder={intl.formatMessage({id: "personalShopper.facts.valuePlaceholder"})} value={draftValue} onChange={(event) => setDraftValue(event.target.value)} />
                            </td>
                            <td style={{padding: "4px 0", whiteSpace: "nowrap", textAlign: "right", verticalAlign: "top"}}>
                                <IconButton size="small" onClick={save}><CheckIcon fontSize="small" /></IconButton>
                                <IconButton size="small" onClick={cancel}><CloseIcon fontSize="small" /></IconButton>
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
            {error && <div style={{color: "#F70D1B", fontSize: "12px", marginTop: "6px"}}>{error}</div>}
        </SideCard>
    );
}

export default FactsCard;
